import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/*
 * ============================================================
 * CART DRAWER CUSTOM BEHAVIOR
 * ============================================================
 *
 * 1. Cart automatically opens after Add to Cart
 * 2. 1 product:
 *    ADD ANY 1 MORE PRODUCT TO UNLOCK 10% DISCOUNT
 *
 * 3. 2+ products:
 *    10% DISCOUNT UNLOCKED 🎉
 *
 * 4. When cart changes from 1 -> 2:
 *    Celebration animation appears INSIDE cart drawer
 *
 * 5. Works even when Horizon re-renders cart drawer
 * ============================================================
 */


/*
 * ============================================================
 * GLOBAL CART STATE
 * ============================================================
 *
 * These variables are outside the custom element.
 *
 * IMPORTANT:
 * Horizon can destroy/recreate <cart-drawer-component>
 * after cart updates.
 *
 * Keeping the state here prevents the count from resetting.
 */

let lastKnownCartCount = null;

let cartObserver = null;

let celebrationTimer = null;


/*
 * ============================================================
 * HELPER: GET CURRENT CART COUNT
 * ============================================================
 */

function getCartCount() {
  /*
   * First try cart drawer badge.
   */

  const badge = document.querySelector(
    '#cart-drawer .theme-drawer__badge'
  );

  if (badge) {
    const badgeText = badge.textContent?.trim();

    const count = Number(badgeText);

    if (Number.isFinite(count)) {
      return count;
    }
  }


  /*
   * Try cart bubble in header.
   */

  const headerBubble = document.querySelector(
    '.cart-bubble__text-count'
  );

  if (headerBubble) {
    const count = Number(
      headerBubble.textContent?.trim()
    );

    if (Number.isFinite(count)) {
      return count;
    }
  }


  /*
   * Empty cart.
   */

  const emptyCart = document.querySelector(
    '#cart-drawer .cart-drawer--empty'
  );

  if (emptyCart) {
    return 0;
  }


  /*
   * If we cannot detect it,
   * return the previous known value.
   */

  return lastKnownCartCount ?? 0;
}


/*
 * ============================================================
 * HELPER: UPDATE OFFER BAR
 * ============================================================
 */

function updateOfferBar(count) {
  const offerBar = document.querySelector(
    '#cart-drawer .cart-offer-progress'
  );

  if (!offerBar) {
    return;
  }


  const message = offerBar.querySelector(
    '[data-cart-offer-message]'
  );

  const progress = offerBar.querySelector(
    '[data-cart-offer-progress]'
  );


  if (!message) {
    return;
  }


  /*
   * 2 OR MORE PRODUCTS
   */

  if (count >= 2) {

    message.textContent =
      '10% DISCOUNT UNLOCKED 🎉';

    offerBar.classList.add(
      'cart-offer-progress--unlocked'
    );

    if (progress) {
      progress.style.width = '100%';
    }

    return;
  }


  /*
   * 1 PRODUCT
   */

  message.textContent =
    'ADD ANY 1 MORE PRODUCT TO UNLOCK 10% DISCOUNT';

  offerBar.classList.remove(
    'cart-offer-progress--unlocked'
  );


  if (progress) {

    if (count === 1) {
      progress.style.width = '50%';
    } else {
      progress.style.width = '0%';
    }

  }
}


/*
 * ============================================================
 * HELPER: OPEN CART DRAWER
 * ============================================================
 */

function openCartDrawer() {

  const cartComponent =
    document.querySelector(
      'cart-drawer-component'
    );


  const drawer =
    cartComponent?.closest('theme-drawer') ||
    document.querySelector(
      '#cart-drawer'
    );


  if (!drawer) {
    return;
  }


  /*
   * Horizon theme-drawer has isOpen/open().
   */

  if (
    typeof drawer.open === 'function' &&
    !drawer.isOpen
  ) {
    drawer.open();
  }
}


/*
 * ============================================================
 * CELEBRATION
 * ============================================================
 */

function showDiscountCelebration() {

  const dialog =
    document.querySelector(
      '#cart-drawer .theme-drawer__dialog'
    );


  if (!dialog) {
    return;
  }


  /*
   * Remove previous celebration.
   */

  dialog
    .querySelector(
      '.cart-discount-celebration'
    )
    ?.remove();


  if (celebrationTimer) {
    window.clearTimeout(
      celebrationTimer
    );
  }


  /*
   * Main celebration container.
   */

  const celebration =
    document.createElement('div');

  celebration.className =
    'cart-discount-celebration';

  celebration.setAttribute(
    'aria-hidden',
    'true'
  );


  /*
   * ==========================================================
   * CENTER MESSAGE
   * ==========================================================
   */

  const message =
    document.createElement('div');

  message.className =
    'cart-discount-celebration__message';

  message.innerHTML = `
    <div class="cart-discount-celebration__emoji">
      🎉
    </div>

    <div class="cart-discount-celebration__title">
      CONGRATULATIONS!
    </div>

    <div class="cart-discount-celebration__discount">
      10% OFF
    </div>

    <div class="cart-discount-celebration__sub">
      DISCOUNT UNLOCKED
    </div>
  `;


  celebration.appendChild(
    message
  );


  /*
   * ==========================================================
   * LEFT FIREWORK
   * ==========================================================
   */

  const leftFirework =
    createFirework('left');

  celebration.appendChild(
    leftFirework
  );


  /*
   * ==========================================================
   * RIGHT FIREWORK
   * ==========================================================
   */

  const rightFirework =
    createFirework('right');

  celebration.appendChild(
    rightFirework
  );


  /*
   * ==========================================================
   * CONFETTI
   * ==========================================================
   */

  const confetti =
    document.createElement('div');

  confetti.className =
    'cart-discount-celebration__confetti';


  for (
    let i = 0;
    i < 42;
    i++
  ) {

    const piece =
      document.createElement('span');

    piece.className =
      'cart-discount-confetti';


    const left =
      Math.random() * 100;

    const delay =
      Math.random() * 0.35;

    const duration =
      1.5 + Math.random() * 1.2;

    const rotation =
      Math.floor(
        Math.random() * 360
      );

    const size =
      4 + Math.floor(
        Math.random() * 5
      );


    piece.style.left =
      `${left}%`;

    piece.style.top =
      `${5 + Math.random() * 18}%`;

    piece.style.width =
      `${size}px`;

    piece.style.height =
      `${size * 1.8}px`;


    piece.style.setProperty(
      '--confetti-delay',
      `${delay}s`
    );

    piece.style.setProperty(
      '--confetti-duration',
      `${duration}s`
    );

    piece.style.setProperty(
      '--confetti-rotation',
      `${rotation}deg`
    );

    piece.style.setProperty(
      '--confetti-x',
      `${(Math.random() - 0.5) * 180}px`
    );

    piece.style.setProperty(
      '--confetti-y',
      `${180 + Math.random() * 220}px`
    );


    confetti.appendChild(
      piece
    );
  }


  celebration.appendChild(
    confetti
  );


  /*
   * Add celebration to CART DIALOG,
   * not to cart-drawer-component.
   *
   * This is important because Horizon can
   * re-render cart-drawer-component.
   */

  dialog.appendChild(
    celebration
  );


  /*
   * Start animation.
   */

  requestAnimationFrame(() => {

    celebration.classList.add(
      'cart-discount-celebration--show'
    );

  });


  /*
   * Remove after animation.
   */

  celebrationTimer =
    window.setTimeout(() => {

      celebration.classList.remove(
        'cart-discount-celebration--show'
      );


      window.setTimeout(() => {

        celebration.remove();

      }, 350);

    }, 3000);
}


/*
 * ============================================================
 * FIREWORK CREATOR
 * ============================================================
 */

function createFirework(side) {

  const burst =
    document.createElement('div');

  burst.className =
    `cart-discount-firework cart-discount-firework--${side}`;


  for (
    let i = 0;
    i < 14;
    i++
  ) {

    const ray =
      document.createElement('span');

    ray.className =
      'cart-discount-firework__ray';


    ray.style.setProperty(
      '--ray-angle',
      `${i * (360 / 14)}deg`
    );


    ray.style.setProperty(
      '--ray-delay',
      `${Math.random() * 0.15}s`
    );


    burst.appendChild(
      ray
    );
  }


  return burst;
}


/*
 * ============================================================
 * REFRESH CART FROM SHOPIFY
 * ============================================================
 */

async function refreshCartCount() {

  try {

    const cartUrl =
      window.Shopify?.routes?.cart_url ||
      '/cart';


    const response =
      await fetch(
        `${cartUrl}.js`,
        {
          method: 'GET',

          headers: {
            Accept:
              'application/json',
          },

          cache: 'no-store',
        }
      );


    if (!response.ok) {
      throw new Error(
        `Cart request failed: ${response.status}`
      );
    }


    const cart =
      await response.json();


    const count =
      Number(
        cart?.item_count ??
        cart?.total_quantity ??
        0
      );


    if (
      !Number.isFinite(count)
    ) {
      return getCartCount();
    }


    return count;

  } catch (error) {

    console.warn(
      '[cart-drawer] Cart refresh failed:',
      error
    );


    return getCartCount();
  }
}


/*
 * ============================================================
 * PROCESS CART COUNT CHANGE
 * ============================================================
 */

function processCartCount(
  newCount
) {

  const oldCount =
    lastKnownCartCount;


  /*
   * Always update offer bar.
   */

  updateOfferBar(
    newCount
  );


  /*
   * First time initialization.
   *
   * Do NOT show celebration just because
   * the page loaded with 2 products.
   */

  if (
    oldCount === null
  ) {

    lastKnownCartCount =
      newCount;

    return;
  }


  /*
   * Detect crossing:
   *
   * 0 -> 1
   * 1 -> 2
   * 2 -> 3
   *
   * Celebration only for:
   *
   * BELOW 2 -> 2 OR MORE
   */

  const unlockedNow =
    oldCount < 2 &&
    newCount >= 2;


  /*
   * Save new count.
   */

  lastKnownCartCount =
    newCount;


  /*
   * Show celebration.
   */

  if (unlockedNow) {

    /*
     * Make sure cart is open first.
     */

    openCartDrawer();


    window.setTimeout(
      () => {

        /*
         * Update offer again immediately
         * before celebration.
         */

        updateOfferBar(
          newCount
        );


        showDiscountCelebration();

      },
      220
    );

  }
}


/*
 * ============================================================
 * OBSERVE HORIZON CART RE-RENDER
 * ============================================================
 *
 * This is the IMPORTANT part.
 *
 * Horizon replaces cart HTML after Add to Cart.
 * MutationObserver detects the new badge/count.
 */

function startCartObserver() {

  if (cartObserver) {
    return;
  }


  /*
   * Initial count.
   */

  const initialCount =
    getCartCount();


  lastKnownCartCount =
    initialCount;


  updateOfferBar(
    initialCount
  );


  /*
   * Observe whole document because Horizon can
   * replace cart-drawer-component.
   */

  cartObserver =
    new MutationObserver(
      () => {

        /*
         * Small delay allows Horizon's
         * Section Rendering API update to finish.
         */

        window.setTimeout(
          () => {

            const currentCount =
              getCartCount();


            /*
             * Ignore impossible/unknown values.
             */

            if (
              !Number.isFinite(
                currentCount
              )
            ) {
              return;
            }


            /*
             * Update only when count changed.
             */

            if (
              currentCount !==
              lastKnownCartCount
            ) {

              processCartCount(
                currentCount
              );

            } else {

              /*
               * Even if count didn't change,
               * make sure offer text is correct.
               */

              updateOfferBar(
                currentCount
              );

            }

          },
          80
        );

      }
    );


  cartObserver.observe(
    document.body,
    {
      childList: true,
      subtree: true,
      characterData: true,
    }
  );
}


/*
 * ============================================================
 * CART DRAWER COMPONENT
 * ============================================================
 */

class CartDrawerComponent
  extends Component {

  #summaryThreshold = 0.5;


  /*
   * ==========================================================
   * THEME DRAWER
   * ==========================================================
   */

  get #themeDrawer() {

    return this.closest(
      'theme-drawer'
    );

  }


  /*
   * ==========================================================
   * DIALOG
   * ==========================================================
   */

  get #dialog() {

    return this.closest(
      'dialog'
    );

  }


  /*
   * ==========================================================
   * CONNECTED
   * ==========================================================
   */

  connectedCallback() {

    super.connectedCallback();


    /*
     * Start global observer.
     */

    startCartObserver();


    /*
     * Listen for Shopify cart event.
     */

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );


    /*
     * Horizon drawer open event.
     */

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );


    /*
     * Make sure current offer is correct.
     */

    const currentCount =
      getCartCount();


    if (
      lastKnownCartCount === null
    ) {

      lastKnownCartCount =
        currentCount;

    }


    updateOfferBar(
      currentCount
    );


    /*
     * If drawer is already open.
     */

    if (
      this.#themeDrawer?.hasAttribute(
        'open'
      )
    ) {

      this.#handleDrawerOpen();

    }

  }


  /*
   * ==========================================================
   * DISCONNECTED
   * ==========================================================
   */

  disconnectedCallback() {

    super.disconnectedCallback();


    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );


    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

  }


  /*
   * ==========================================================
   * DRAWER OPEN
   * ==========================================================
   */

  #handleDrawerOpen = () => {

    this.#updateStickyState();


    customElements
      .whenDefined(
        'shopify-payment-terms'
      )
      .then(() => {

        const cta =
          this.querySelector(
            'shopify-payment-terms'
          )
            ?.shadowRoot
            ?.querySelector(
              '#shopify-installments-cta'
            );


        cta?.addEventListener(
          'click',
          () =>
            this.#themeDrawer?.close(),
          {
            once: true,
          }
        );

      });

  };


  /*
   * ==========================================================
   * CART LINES UPDATE
   * ==========================================================
   */

  #handleCartLinesUpdate =
    (event) => {

      /*
       * For remove/update:
       * just refresh offer + sticky state.
       */

      if (
        event.action !== 'add'
      ) {

        event.promise
          ?.then(() => {

            window.setTimeout(
              async () => {

                const count =
                  await refreshCartCount();


                processCartCount(
                  count
                );


                this.#updateStickyState();

              },
              100
            );

          })
          .catch((error) => {

            console.warn(
              '[cart-drawer] Cart update error:',
              error
            );

          });


        return;
      }


      /*
       * ======================================================
       * ADD TO CART
       * ======================================================
       */

      const sourceModal =
        event.target instanceof Element
          ? event.target.closest(
              'dialog:modal'
            )
          : null;


      const finishAdd =
        async () => {

          /*
           * Wait until Shopify cart update
           * has completed.
           */

          try {

            await event.promise;

          } catch (error) {

            console.warn(
              '[cart-drawer] Add to cart error:',
              error
            );

            return;

          }


          /*
           * Small delay for Horizon
           * Section Rendering update.
           */

          window.setTimeout(
            async () => {

              const count =
                await refreshCartCount();


              /*
               * Update global state.
               *
               * Observer may already have detected
               * it, but processCartCount safely
               * handles the final state.
               */

              if (
                lastKnownCartCount === null
              ) {

                lastKnownCartCount =
                  count;

                updateOfferBar(
                  count
                );

              } else if (
                count !==
                lastKnownCartCount
              ) {

                processCartCount(
                  count
                );

              } else {

                updateOfferBar(
                  count
                );

              }


              /*
               * ALWAYS open cart after add.
               */

              openCartDrawer();


              /*
               * Update sticky summary.
               */

              window.requestAnimationFrame(
                () => {

                  this.#updateStickyState();

                }
              );

            },
            180
          );

        };


      /*
       * Quick Add / modal case.
       */

      if (
        sourceModal?.open
      ) {

        sourceModal.addEventListener(
          'close',
          finishAdd,
          {
            once: true,
          }
        );

      } else {

        finishAdd();

      }

    };


  /*
   * ==========================================================
   * STICKY SUMMARY
   * ==========================================================
   */

  #updateStickyState() {

    const dialog =
      this.#dialog;


    if (!dialog) {
      return;
    }


    const content =
      dialog.querySelector(
        '.cart-drawer__content'
      );


    const summary =
      dialog.querySelector(
        '.cart-drawer__summary'
      );


    if (
      !content ||
      !summary
    ) {

      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;

    }


    const drawerHeight =
      dialog.getBoundingClientRect()
        .height;


    const summaryHeight =
      summary.getBoundingClientRect()
        .height;


    const ratio =
      drawerHeight > 0
        ? summaryHeight /
          drawerHeight
        : 0;


    dialog.setAttribute(
      'cart-summary-sticky',
      ratio >
        this.#summaryThreshold
        ? 'false'
        : 'true'
    );

  }

}


/*
 * ============================================================
 * REGISTER COMPONENT
 * ============================================================
 */

if (
  !customElements.get(
    'cart-drawer-component'
  )
) {

  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );

}


/*
 * ============================================================
 * FALLBACK INITIALIZATION
 * ============================================================
 *
 * In case cart component is loaded after this module.
 */

if (
  document.readyState ===
  'loading'
) {

  document.addEventListener(
    'DOMContentLoaded',
    () => {

      startCartObserver();

    },
    {
      once: true,
    }
  );

} else {

  startCartObserver();

}